import {
	PopoverContent,
	PopoverContentProps,
	PopoverPortal,
	PopoverProps,
	Root as PopoverRoot,
	PopoverTrigger,
	PopoverTriggerProps,
} from '@radix-ui/react-popover';
import { Command } from 'cmdk';
import { HTMLProps, ReactNode, useState } from 'react';
import DropdownUi, {
	DropdownUiTriggerProps,
} from 'src/components/primitives/DropdownUi/DropdownUi';
import { DROPDOWN_SIDE_OFFSET } from 'src/components/primitives/DropdownUi/consts';
import SearchBar from 'src/components/primitives/SearchBar/SearchBar';
import { cm } from 'src/utils/classNames';
import { WithChildren, WithClassName, WithRef } from 'src/utils/UtilityTypes';
import { Merge } from 'type-fest';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';

function Root({
	open: openFromProps,
	onOpenChange: onOpenChangeFromProps,
	children,
}: PopoverProps) {
	const [openFromState, setOpenFromState] = useState(false);

	const open = openFromProps ?? openFromState;
	const onOpenChange = onOpenChangeFromProps ?? setOpenFromState;

	return (
		<PopoverRoot open={open} onOpenChange={onOpenChange} modal>
			{children}
		</PopoverRoot>
	);
}

export interface ComboBoxTriggerProps extends WithRef<
	Merge<DropdownUiTriggerProps, PopoverTriggerProps>,
	HTMLButtonElement
> {
	hideChevron?: boolean;
	dataTestId?: string;
}

function Trigger({
	size = 'sm',
	className,
	labelClassName,
	valueClassName,
	label,
	children,
	disabled,
	hideChevron,
	dataTestId,
	...rest
}: ComboBoxTriggerProps) {
	return (
		<PopoverTrigger asChild {...rest}>
			<DropdownUi.Trigger
				size={size}
				className={className}
				labelClassName={labelClassName}
				valueClassName={valueClassName}
				label={label}
				disabled={disabled}
				data-testid={dataTestId}
			>
				{children}
				{!hideChevron && <DropdownUi.Chevron />}
			</DropdownUi.Trigger>
		</PopoverTrigger>
	);
}

export interface ComboboxOptionsProps extends WithChildren<
	WithClassName<WithRef<PopoverContentProps, HTMLDivElement>>
> {
	id: string;
	query: string;
	setQuery: (q: string) => void;
	value: string | undefined;
	onValueChange: (value: string) => void;
	searchBarPlaceholder: string;
	scrollContainerClassName?: string;
	/**
	 * Renders the passed node between the search bar and the list of options.
	 */
	header?: ReactNode;
}

function Options({
	id,
	query,
	setQuery,
	value,
	onValueChange,
	searchBarPlaceholder,
	className,
	scrollContainerClassName,
	children,
	sideOffset = DROPDOWN_SIDE_OFFSET,
	header,
	...rest
}: ComboboxOptionsProps) {
	const isMobileScreenSize = useIsMobileScreenSize();

	return (
		<PopoverPortal>
			<PopoverContent
				asChild
				sideOffset={sideOffset}
				// Don't autofocus the input on mobile because it will cause the keyboard to open.
				onOpenAutoFocus={(e) => isMobileScreenSize && e.preventDefault()}
				{...rest}
			>
				<DropdownUi.Content
					className={cm('min-w-(--radix-popover-trigger-width)', className)}
				>
					<Command
						className="flex flex-col gap-y-2"
						// We just need to know the currently selected value for this case.
						// Command is being controlled via the Command.Item `onSelect` prop.
						// Using `defaultValue` here since `value` changes whenever an item is selected via keyboard nav.
						defaultValue={value}
						onValueChange={onValueChange}
						loop
					>
						<div id={`${id}-search-bar-container`} className="p-sm pb-0">
							<SearchBar
								id={id}
								size="sm"
								query={query}
								setQuery={setQuery}
								placeholder={searchBarPlaceholder}
							/>
						</div>
						<Command.List>
							{header}
							<Command.Empty className="p-md typo-b4 text-text-tertiary">
								No results found.
							</Command.Empty>
							<div
								className={cm(
									'hidden-scroll flex flex-col overflow-y-auto',
									scrollContainerClassName
								)}
							>
								{children}
							</div>
						</Command.List>
					</Command>
				</DropdownUi.Content>
			</PopoverContent>
		</PopoverPortal>
	);
}

export interface ComboboxOptionProps extends WithClassName<
	WithRef<Omit<HTMLProps<HTMLDivElement>, 'onSelect'>>
> {
	children: ReactNode;
	value: string;
	disabled?: boolean;
	isSelected: boolean;
	noSelectedCheckmark?: boolean;
	onValueChange: (value: string) => void;
	dataTestId?: string;
}

function Option({
	children,
	disabled,
	className,
	onValueChange,
	isSelected,
	noSelectedCheckmark,
	dataTestId,
	...rest
}: ComboboxOptionProps) {
	return (
		<Command.Item
			asChild
			onSelect={onValueChange}
			disabled={disabled}
			{...rest}
		>
			<DropdownUi.Item
				className={className}
				disabled={disabled}
				isSelected={isSelected}
				data-testid={dataTestId}
			>
				{children}
				{isSelected && !noSelectedCheckmark && <DropdownUi.SelectedCheckmark />}
			</DropdownUi.Item>
		</Command.Item>
	);
}

export const ComboBox = {
	Root,
	Trigger,
	Options,
	Option,
};
